/**
 * LuxeKaching — read the Kaching Bundles deal for a product from the page and price it
 * exactly the way Kaching's own storefront widget does, so our custom bundle UIs show the
 * same numbers as Kaching (and therefore as the cart, where Kaching's discount applies).
 *
 * Data source: the Kaching app embed prints, on every product page,
 *   <script id="kaching-bundles-config" type="application/json">            (currencyRate, featureFlags)
 *   <script class="kaching-bundles-deal-block-settings" data-product-id="…"> (dealBars, priceRounding…)
 *
 * Pricing mirrors Kaching's `wa()` + `Ri()` (kaching-bundles-block.js, extension 2040).
 * All money values are integer minor units (cents/pence) in the visitor's market currency.
 */
(function () {
  if (window.LuxeKaching) return;

  const readJson = (el) => {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };

  function getConfig() {
    return readJson(document.getElementById('kaching-bundles-config')) || {};
  }

  function getDeal(productId) {
    if (!productId) return null;
    return readJson(
      document.querySelector(`script.kaching-bundles-deal-block-settings[data-product-id="${productId}"]`)
    );
  }

  /** Quantity-break tiers only, sorted by quantity. */
  function getQuantityBars(deal) {
    if (!deal || !Array.isArray(deal.dealBars)) return [];
    return deal.dealBars
      .filter((b) => (b.dealBarType || 'quantity-break') === 'quantity-break' && Number(b.quantity) > 0)
      .sort((a, b) => Number(a.quantity) - Number(b.quantity));
  }

  /** Kaching `Ri`: round a minor-unit amount up to the configured ending. */
  function roundTo(n, precision) {
    switch (precision) {
      case '.99':
        return n > 99 ? 100 * Math.ceil(n / 100) - 1 : n;
      case '.95':
        return n > 95 ? 100 * Math.ceil(n / 100) - 5 : n;
      case '.90':
        return n > 90 ? 100 * Math.ceil(n / 100) - 10 : n;
      case '.x9':
        return n > 9 ? 10 * Math.ceil(n / 10) - 1 : n;
      case '.x0':
        return 10 * Math.ceil(n / 10);
      default:
        return 100 * Math.ceil(n / 100);
    }
  }

  /**
   * Bundle total for `qty` units, where `prices` is an array of unit prices (one per unit,
   * so a mixed-colour bundle can pass each variant's price). Mirrors Kaching `wa()`.
   */
  function bundleTotal(bar, prices, deal, config) {
    const qty = prices.length;
    if (!bar || !qty) return prices.reduce((a, b) => a + b, 0);
    const cfg = config || getConfig();
    const flags = cfg.featureFlags || {};
    const rate = Number(cfg.currencyRate) || 1;
    const type = bar.discountType;
    const value = Number(bar.discountValue) || 0;
    const full = prices.reduce((a, b) => a + b, 0);

    let total;
    if (type === 'specific') {
      let n = 100 * value * rate;
      const dq = Number(bar.quantity) || qty;
      if (qty > dq) n = Math.ceil((n / dq) * qty);
      total = n;
    } else if (type === 'percentage') {
      if (flags.percentage_cents_rounding_workaround) {
        total = Math.ceil((full * (100 - value)) / 100);
      } else {
        total = prices.reduce((sum, p) => sum + Math.ceil((p * (100 - value)) / 100), 0);
      }
    } else if (type === 'amount') {
      total = full - Math.round(100 * value) * rate * qty;
    } else {
      total = full;
    }
    total = Math.max(0, Math.min(total, full));

    const v2 = !!flags.price_rounding_v2;
    if (deal && deal.priceRounding && (type !== 'default' || v2)) {
      const precision = deal.priceRoundingPrecision;
      if (deal.showPricesPerItem) {
        const h = roundTo(total / qty, precision) * qty;
        if (v2 && Math.round(h) > Math.round(full) && total / qty > 100) {
          return Math.round(roundTo(total / qty - 100, precision) * qty);
        }
        return Math.round(Math.min(h <= full ? h : total, full));
      }
      const h = roundTo(total, precision);
      if (v2 && Math.round(h) > Math.round(full) && total > 100) return Math.round(roundTo(total - 100, precision));
      return Math.round(Math.min(h <= full ? h : total, full));
    }
    return Math.round(total);
  }

  /** Per-item price as Kaching shows it (rounded up to the minor unit). */
  function perItem(total, qty) {
    return qty > 0 ? Math.ceil(total / qty) : total;
  }

  window.LuxeKaching = { getConfig, getDeal, getQuantityBars, bundleTotal, perItem, roundTo };
})();
